import { ReactNode, createContext, useState, useMemo } from 'react';

import { formatDateWzTimezone, getValidDate } from 'src/utils/dateTime';

//
//

type DateCell = Date | null;

type DateRange = { from: DateCell; to: DateCell };

export type DateFilterContextProps = {
  handleChange: (newValue: DateRange) => void;
  dateRange: DateRange;
  localDateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  setLocalDateRange: (value: DateRange) => void;
  dateFrom: string;
  dateTo: string;
  cumulativeDateFrom: string;
  cumulativeDateTo: string;
  setUpdateTableData: (v: any) => void;
  selectedTimezone: TIMEZONE;
  setSelectedTimezone: (timezone: TIMEZONE) => void;
};

export enum TIMEZONE {
  UTC = 'UTC',
  LOCAL = 'LOCAL',
}

const DateFilterContext = createContext<DateFilterContextProps>({
  handleChange: () => {},
  dateRange: {
    from: null,
    to: null,
  },
  localDateRange: { from: null, to: null },
  setDateRange: (value: DateRange) => {},
  setLocalDateRange: (value: DateRange) => {},
  dateFrom: '',
  dateTo: '',
  cumulativeDateFrom: '',
  cumulativeDateTo: '',
  setUpdateTableData: () => {},
  selectedTimezone: TIMEZONE.LOCAL,
  setSelectedTimezone: () => {},
});

type DateFilterProviderProps = {
  children: ReactNode;
};

const addMonths = (input: any, months: any) => {
  const date = new Date(input);
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  return date;
};

function DateFilterProvider({ children }: DateFilterProviderProps) {
  const today = new Date(new Date().setHours(0,0,0,0));
  const sixMonthsAgo = addMonths(today, -6);
  const [updateTableData, setUpdateTableData] = useState<{ func: () => void }>({ func: () => {} });

  const [dateRange, setDateRange] = useState<DateRange>({ from: sixMonthsAgo, to: today });
  const [localDateRange, setLocalDateRange] = useState<DateRange>({
    from: dateRange.from,
    to: dateRange.to,
  });
  const [selectedTimezone, setSelectedTimezone] = useState<TIMEZONE>(TIMEZONE.LOCAL);

  const handleChange = (newValue: DateRange) => {
    if (newValue) {
      setDateRange({ from: getValidDate(newValue.from), to: getValidDate(newValue.to) });
      updateTableData && updateTableData.func();
    }
  };

  const dateFrom = useMemo(() => 
    formatDateWzTimezone(dateRange.from as Date, selectedTimezone, true),
  [dateRange?.from, selectedTimezone]);

  const dateTo = useMemo(() => 
    formatDateWzTimezone(dateRange.to as Date, selectedTimezone),
  [dateRange?.to, selectedTimezone]);

  const cumulativeDateFrom = useMemo(() => {
    return formatDateWzTimezone(new Date('01/01/1970'), selectedTimezone, true);
  }, [selectedTimezone]);

  const cumulativeDateTo = useMemo(() => dateFrom || '', [dateFrom]);

  return (
    <DateFilterContext.Provider
      value={{
        dateRange,
        handleChange,
        setDateRange,
        dateFrom,
        dateTo,
        cumulativeDateFrom,
        cumulativeDateTo,
        setUpdateTableData,
        localDateRange,
        setLocalDateRange,
        selectedTimezone,
        setSelectedTimezone,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
}

export { DateFilterProvider, DateFilterContext };
